import { listen, type EventCallback } from "@tauri-apps/api/event";

export type TauriUnlisten = () => void | Promise<void>;

/** Retries an unlisten that ran before Tauri installed the listener entry. */
const UNLISTEN_RETRY_MS = 50;
const UNLISTEN_ATTEMPTS = 6;

/**
 * Removes a Tauri listener, Rust side included. Tauri registers the webview
 * side of a listener with a script that can run after `listen` has resolved;
 * an unlisten landing in that window throws in `unregisterListener` before it
 * reaches Rust, and the late script then installs the listener for good — a
 * Rust listener every later emit still targets. Retrying once the script has
 * run removes it. Failures stay silent: during webview teardown there is
 * nothing left to remove.
 */
export function safeTauriUnlisten(unlisten: TauriUnlisten): void {
  attemptUnlisten(unlisten, UNLISTEN_ATTEMPTS);
}

function attemptUnlisten(unlisten: TauriUnlisten, attemptsLeft: number): void {
  const retry = () => {
    if (attemptsLeft > 1)
      setTimeout(
        () => attemptUnlisten(unlisten, attemptsLeft - 1),
        UNLISTEN_RETRY_MS,
      );
  };
  try {
    void Promise.resolve(unlisten()).catch(retry);
  } catch {
    retry();
  }
}

/**
 * Subscribes to a Tauri event for the life of an effect. The returned cleanup
 * silences the handler at once, so a delivery before `safeTauriUnlisten` has
 * reached Rust never acts on the component's state.
 */
export function listenWhileMounted<T>(
  event: string,
  handler: EventCallback<T>,
): () => void {
  let active = true;
  const unlisten = listen<T>(event, (payload) => {
    if (active) handler(payload);
  });
  return () => {
    active = false;
    void unlisten.then(safeTauriUnlisten, () => {});
  };
}
